import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useEffect, useMemo, useRef, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import TextosConfirmacao from '@/components/recepcao/TextosConfirmacao';
import { useAuth } from '@/auth/AuthProvider';
import { formatarTelefone } from '@/lib/telefone';
import { lerAgendaDoPdf } from '@/recepcao/agendaPdf';
import {
  MODOS,
  ativaNoModo,
  resumir,
} from '@/recepcao/agendaParser';
import {
  MODELOS_POR_MODO,
  modelosDoModo,
  PESSOA_EXEMPLO,
  TEXTOS_PADRAO,
  agruparPorPessoa,
  montarTexto,
} from '@/recepcao/mensagemAgenda';
import {
  CHAVE_ENVIO_AGENDA,
  guardarEstado,
  lerEstado,
  useEstadoPreservado,
} from '@/lib/estadoDeSessao';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import { enviarConfirmacaoAgenda } from '@/services/conversasService';
import { tratarErro } from '@/lib/erros';
import AvisoCarga from '@/components/AvisoCarga';

/**
 * Importação da agenda do Careflow (PLANO 8.1).
 *
 * A Fase 1 lê o PDF e mostra o que entendeu; a Fase 2 envia — e envia de
 * verdade, para paciente de verdade. Por isso nada aqui dispara sozinho: é
 * preciso escolher o arquivo, conferir a lista, escolher o texto e confirmar
 * num diálogo que diz quantas PESSOAS vão receber.
 *
 * O PDF é lido no navegador e não sobe para servidor nenhum: agenda tem nome,
 * telefone e observação clínica de paciente (PLANO seção 9 — LGPD).
 */

/** Intervalo entre um paciente e o seguinte. */
const PAUSA_MIN_MS = 5_000;
const PAUSA_MAX_MS = 10_000;

/**
 * Chaves do estado preservado entre montagens.
 *
 * Tudo EM MEMÓRIA: esta lista tem nome e telefone de paciente, e não pode ser
 * escrita em `sessionStorage` (PLANO seção 9 — LGPD). Ver `lib/estadoDeSessao`.
 */
const CHAVES = {
  consultas: 'agenda:consultas',
  marcadas: 'agenda:marcadas',
  data: 'agenda:data',
  arquivo: 'agenda:arquivo',
  modelo: 'agenda:modelo',
  modo: 'agenda:modo',
  // ★ WU/U2: a grafia vem do store — o AppShell lê a MESMA chave para travar o botão.
  envio: CHAVE_ENVIO_AGENDA,
};

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

/** Pausa aleatória: intervalo fixo tem a cara de robô que o WhatsApp procura. */
const pausaAleatoria = () =>
  PAUSA_MIN_MS + Math.floor(Math.random() * (PAUSA_MAX_MS - PAUSA_MIN_MS + 1));

const MENSAGENS_ERRO = {
  'functions/failed-precondition':
    'O número da recepção não está conectado ao WhatsApp. Conecte em Canais e tente de novo.',
  'functions/permission-denied': 'Seu papel não atende o espaço Recepção.',
  'functions/unauthenticated': 'Sua sessão expirou. Entre de novo.',
  'functions/unavailable': 'Não foi possível falar com o servidor.',
  'functions/invalid-argument': 'Dados inválidos para este envio.',
};

const explicar = (erro) =>
  MENSAGENS_ERRO[erro?.code] ?? erro?.message ?? 'Falha desconhecida no envio.';

/** Hoje em `aaaa-mm-dd`, para o <input type="date"> já vir preenchido. */
function hojeIso() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * Tenta descobrir a data pelo nome do arquivo (`relatorio_2026-08-22.pdf`).
 *
 * É palpite, não verdade: o campo continua editável e visível. Mas errar a data
 * do lote seria confirmar consulta do dia errado, então adivinhar bem aqui
 * poupa o erro mais caro desta tela.
 */
function dataDoNome(nome) {
  const m = /(20\d{2})[-_]?(\d{2})[-_]?(\d{2})/.exec(String(nome ?? ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

const dataLonga = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  const data = new Date(Number(a), Number(m) - 1, Number(d));
  return data.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

export default function ConfirmarAgendaPage() {
  const [falhaTextos, setFalhaTextos] = useState(false); // ★ WT lote 1
  const { clinicaId, papel } = useAuth();

  // Preservados entre montagens: ler o PDF de novo a cada ida ao inbox e volta
  // era o caminho para a recepção reconferir 27 linhas por engano — ou pior,
  // redisparar um lote achando que o primeiro não tinha saído.
  const [configClinica, setConfigClinica] = useState(null);
  const sistema = nomeDoSistema(configClinica);
  const [consultas, setConsultas] = useEstadoPreservado(CHAVES.consultas, []);
  const [marcadas, setMarcadas] = useEstadoPreservado(CHAVES.marcadas, () => new Set());
  const [dataLote, setDataLote] = useEstadoPreservado(CHAVES.data, hojeIso);
  const [arquivo, setArquivo] = useEstadoPreservado(CHAVES.arquivo, '');
  // O modelo acompanha o resto: voltar com a lista intacta mas o seletor de
  // volta em "Primeira confirmação" faria a recepção disparar o texto errado.
  /**
   * O modo decide QUAIS consultas entram e QUAL texto sai. Preservado como o
   * resto: voltar de outra tela não pode reverter a recepção para confirmação
   * quando ela estava reagendando.
   */
  const [modo, setModo] = useEstadoPreservado(CHAVES.modo, 'confirmacao');
  const modelos = modelosDoModo(modo);
  const [modelo, setModelo] = useEstadoPreservado(CHAVES.modelo, MODELOS_POR_MODO.confirmacao[0].id);
  const [envio, setEnvio] = useEstadoPreservado(CHAVES.envio, null);

  // Efêmeros de propósito: dizem respeito a esta visita, não ao lote.
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState(null);
  const [textos, setTextos] = useState(TEXTOS_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  // Só o gestor grava em `clinicas/{id}` — as rules recusam os demais papéis.
  const podeSalvar = papel === 'gestor' || papel === 'superadmin';

  // Trava contra envio duplo: o estado do React não muda a tempo de barrar um
  // segundo clique dentro do mesmo tick.
  const enviando = useRef(false);

  // Token do lote em andamento. O laço guarda ESTE objeto no closure, então
  // marcá-lo de fora o interrompe mesmo depois do componente desmontar.
  const loteRef = useRef(null);

  /**
   * Sair da tela PARA o lote — e agora para de verdade.
   *
   * Antes o laço continuava rodando invisível depois do desmonte, e os
   * `setEnvio` caíam no vazio. Com o progresso preservado, isso ficaria pior:
   * voltar mostraria "Enviando 3 de 7…" congelado para sempre, com o botão
   * travado, sem como saber se ainda estava saindo mensagem. O resumo é
   * congelado como INTERROMPIDO, e o que já saiu continua contado.
   */
  useEffect(
    () => () => {
      if (loteRef.current) loteRef.current.abortado = true;
      const ultimo = lerEstado(CHAVES.envio, null);
      if (ultimo?.rodando) {
        guardarEstado(CHAVES.envio, { ...ultimo, rodando: false, interrompido: true });
      }
    },
    [],
  );

  /** Textos salvos da clínica sobrescrevem os padrões, campo a campo. */
  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(
      clinicaId,
      (clinica) => {
        setConfigClinica(clinica?.config ?? null); // VX: o nome do sistema de gestão
        const salvos = clinica?.config?.textosConfirmacao;
        if (!salvos) return;
        setTextos((atuais) => ({
          primeira: salvos.primeira ?? atuais.primeira,
          reconfirmacao: salvos.reconfirmacao ?? atuais.reconfirmacao,
          cancelamento: salvos.cancelamento ?? atuais.cancelamento,
          reagendamento: salvos.reagendamento ?? atuais.reagendamento,
        }));
      },
      // ★★ WT lote 1: o corpo era SÓ um comentário — o varredor da medição não o contou
      // como engolido (a quebra de linha escondeu o padrão), e foi a guarda nova que o
      // achou. ⚠ Sem a config, os textos de confirmação caem no padrão sem avisar.
      tratarErro('os textos de confirmação', () => setFalhaTextos(true)),
    );
  }, [clinicaId]);

  /**
   * Trocar de modo muda o conjunto inteiro, não só um rótulo.
   *
   * Duas coisas TÊM de acompanhar, ou a tela fica mentindo:
   * 1. o **modelo**, porque o id do modo anterior não existe no novo (deixar
   *    'primeira' selecionada em Reagendamento renderizaria textarea vazia);
   * 2. as **marcadas**, porque elas foram derivadas da predicate do outro modo —
   *    sem isto a recepção mandaria reagendamento para quem está em aberto.
   */
  function trocarModo(novo) {
    if (novo === modo) return;
    setModo(novo);
    setModelo(modelosDoModo(novo)[0].id);
    setMarcadas(
      new Set(
        consultas.reduce((acc, c, i) => (ativaNoModo(c, novo) && c.celular ? [...acc, i] : acc), []),
      ),
    );
  }

  const rotuloAtivas = modo === 'reagendamento' ? 'Não compareceu' : 'Em aberto';
  const notaAtivas = modo === 'reagendamento' ? 'são as que vão reagendar' : 'são as que pedem confirmação';
  const verboLote = modo === 'reagendamento' ? 'reagendamentos' : 'confirmações';

  const resumo = useMemo(() => resumir(consultas, modo), [consultas, modo]);

  // O agrupamento é a unidade real do envio: quem tem três consultas no dia
  // recebe UMA mensagem, com o horário mais cedo. Toda contagem visível sai
  // daqui, para a tela nunca prometer um número e enviar outro.
  const pessoas = useMemo(() => agruparPorPessoa(consultas, marcadas), [consultas, marcadas]);

  async function aoEscolher(evento) {
    const file = evento.target.files?.[0];
    if (!file) return;

    setErro(null);
    setLendo(true);
    setConsultas([]);
    setMarcadas(new Set());
    setEnvio(null);
    try {
      const { consultas: lidas } = await lerAgendaDoPdf(file);
      if (lidas.length === 0) {
        setErro(
          `Não encontrei consultas neste PDF. Confira se é o relatório de agenda do ${sistema}.`,
        );
      }
      setConsultas(lidas);
      // Marcadas por padrão: só as ATIVAS NO MODO e COM celular. Marcar quem não tem
      // celular seria prometer uma confirmação que não teria como sair.
      setMarcadas(
        new Set(lidas.reduce((acc, c, i) => (ativaNoModo(c, modo) && c.celular ? [...acc, i] : acc), [])),
      );
      setArquivo(file.name);
      const doNome = dataDoNome(file.name);
      if (doNome) setDataLote(doNome);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao ler o PDF da agenda:', e);
      setErro('Não foi possível ler este arquivo. Ele é um PDF válido?');
    } finally {
      setLendo(false);
      // Permite reescolher o MESMO arquivo depois de corrigir algo no Careflow:
      // sem isto o input não dispara change para o mesmo nome.
      evento.target.value = '';
    }
  }

  const alternar = (i) =>
    setMarcadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(i)) proximo.delete(i);
      else proximo.add(i);
      return proximo;
    });

  const marcarTodas = (ligar) =>
    setMarcadas(
      ligar
        ? new Set(consultas.reduce((a, c, i) => (c.celular ? [...a, i] : a), []))
        : new Set(),
    );

  function mudarTexto(campo, valor) {
    setTextos((atuais) => ({ ...atuais, [campo]: valor }));
    setSalvo(false);
  }

  async function salvarTextos() {
    setSalvando(true);
    setErro(null);
    try {
      // Campo pontilhado: preserva funil, horário de atendimento e o resto da
      // config, que gravar o objeto inteiro apagaria.
      await atualizarCampoConfig(clinicaId, 'textosConfirmacao', textos);
      setSalvo(true);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar os textos:', e);
      setErro('Não foi possível salvar os textos. Só o gestor pode gravar essa configuração.');
    } finally {
      setSalvando(false);
    }
  }

  const campoDoModelo = modelos.find((m) => m.id === modelo)?.campo ?? modelos[0].campo;

  /**
   * Dispara o lote, uma pessoa por vez, com pausa entre elas.
   *
   * A cadência é o ponto: o número da recepção é uma linha não oficial, e uma
   * rajada de mensagens idênticas em segundos é o padrão que faz o WhatsApp
   * banir (PLANO 5.1). Falha de um paciente não interrompe o lote — a recepção
   * precisa dos outros dezenove confirmados, e a lista de falhas fica no fim
   * para reenvio manual.
   */
  async function dispararLote() {
    if (enviando.current) return;
    enviando.current = true;
    setConfirmando(false);
    setErro(null);

    const total = pessoas.length;
    const lote = { abortado: false };
    loteRef.current = lote;
    setEnvio({ total, feitas: 0, enviadas: 0, falhas: [], rodando: true });

    for (let i = 0; i < total; i += 1) {
      // Saiu da tela no meio: para aqui. O envio em voo não dá para cancelar,
      // mas o próximo não sai.
      if (lote.abortado) {
        enviando.current = false;
        return;
      }
      const pessoa = pessoas[i];
      setEnvio((e) => ({ ...e, atual: i + 1, nomeAtual: pessoa.nomeCompleto }));

      try {
        await enviarConfirmacaoAgenda({
          clinicaId,
          papel: 'recepcao',
          telefone: pessoa.telefone,
          nome: pessoa.nomeCompleto,
          texto: montarTexto(textos[campoDoModelo], pessoa, dataLote),
        });
        setEnvio((e) => ({ ...e, feitas: e.feitas + 1, enviadas: e.enviadas + 1 }));
      } catch (e) {
        tratarErro('a confirmação de agenda', null, 'callable')(e);
        setEnvio((est) => ({
          ...est,
          feitas: est.feitas + 1,
          falhas: [...est.falhas, { pessoa, motivo: explicar(e) }],
        }));
      }

      // Não espera depois do último: seriam até 10s de "enviando" com o lote
      // já terminado.
      if (i < total - 1) await esperar(pausaAleatoria());
    }

    setEnvio((e) => ({ ...e, rodando: false, atual: null, nomeAtual: null }));
    enviando.current = false;
  }

  const rodando = Boolean(envio?.rodando);

  return (
    <div className="pagina">
      <header className="pagina-cabecalho">
        <h1>Confirmar agenda</h1>
        {/* ★ WT: sem os textos salvos a tela usa os padrões — e agora DIZ isso. */}
        {falhaTextos && <AvisoCarga oQue="os textos de confirmação desta clínica" />}
        <p className="painel-sub">
          Leia o relatório do {sistema}, confira a lista e envie as confirmações pelo WhatsApp
          da recepção.
        </p>
      </header>

      <section className="secao">
        <div className="cartao">
          <div className="linha-upload">
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={aoEscolher}
              disabled={lendo || rodando}
              aria-label="Relatório de agenda em PDF"
            />
            {lendo && <span className="campo-ajuda">Lendo o PDF…</span>}
            {arquivo && !lendo && <span className="campo-ajuda">{arquivo}</span>}
          </div>
          <p className="campo-ajuda" style={{ marginTop: 8 }}>
            O arquivo é lido <strong>no seu navegador</strong> e não sai daqui. A coluna de
            observações não é carregada.
          </p>
        </div>
      </section>

      {/* O modo decide o relatório que se sobe e o texto que sai. Fica em
          destaque, logo abaixo do upload: escolher errado manda reagendamento
          para quem tem consulta marcada. */}
      <section className="secao">
        <div className="cartao seletor-modo">
          <span className="secao-titulo pequeno">O que você vai enviar?</span>
          <div className="modo-opcoes">
            {MODOS.map((m) => (
              <label key={m.id} className={modo === m.id ? 'modo-opcao ativa' : 'modo-opcao'}>
                <input
                  type="radio"
                  name="modo-agenda"
                  value={m.id}
                  checked={modo === m.id}
                  onChange={() => trocarModo(m.id)}
                />
                <span>
                  <strong>{m.rotulo}</strong>
                  <span className="campo-ajuda">{m.sub}</span>
                </span>
              </label>
            ))}
          </div>
          {modo === 'reagendamento' && (
            <p className="campo-ajuda">
              Suba o relatório do <strong>dia anterior</strong> — é ele que traz os status já
              realizados. Só as <strong>NÃO COMPARECEU</strong> entram; canceladas ficam de fora.
            </p>
          )}
        </div>
      </section>

      {erro && <p className="erro">{erro}</p>}

      {consultas.length > 0 && (
        <>
          <section className="secao">
            <div className="indicadores">
              <div className="indicador">
                <span className="indicador-rotulo">Linhas lidas</span>
                <span className="indicador-valor">{resumo.total}</span>
              </div>
              <div className="indicador">
                <span className="indicador-rotulo">{rotuloAtivas}</span>
                <span className="indicador-valor">{resumo.ativas}</span>
                <span className="indicador-nota">{notaAtivas}</span>
              </div>
              <div className="indicador">
                <span className="indicador-rotulo">Sem celular</span>
                <span className="indicador-valor">{resumo.semCelular}</span>
                <span className="indicador-nota">
                  {resumo.semCelular === 0
                    ? 'todas têm celular'
                    : resumo.ativasSemCelular > 0
                      ? `${resumo.ativasSemCelular} ${rotuloAtivas.toLowerCase()} — não dá para escrever`
                      : `nenhuma delas está em "${rotuloAtivas.toLowerCase()}"`}
                </span>
              </div>
              <div className="indicador">
                <span className="indicador-rotulo">Vão receber</span>
                <span className="indicador-valor">{pessoas.length}</span>
                <span className="indicador-nota">
                  {marcadas.size === pessoas.length
                    ? 'pessoas'
                    : `pessoas, de ${marcadas.size} consultas marcadas`}
                </span>
              </div>
            </div>
          </section>

          <section className="secao">
            <header className="secao-barra">
              <div>
                <h2 className="secao-titulo">Data da agenda</h2>
                <p className="secao-sub">
                  A agenda é de um dia só. Esta data vai na mensagem, na variável{' '}
                  <code>{'{dia}'}</code>.
                </p>
              </div>
            </header>
            <div className="cartao">
              <div className="campo" style={{ maxWidth: 260 }}>
                <label htmlFor="ca-data">Dia das consultas</label>
                <input
                  id="ca-data"
                  type="date"
                  value={dataLote}
                  onChange={(e) => setDataLote(e.target.value)}
                  disabled={rodando}
                />
                {dataLote && <p className="campo-ajuda">{dataLonga(dataLote)}</p>}
              </div>
            </div>
          </section>

          <TextosConfirmacao
            textos={textos}
            aoMudarTexto={mudarTexto}
            modelos={modelos}
            modelo={modelo}
            aoMudarModelo={setModelo}
            dia={dataLote}
            pessoaPrevia={pessoas[0] ?? PESSOA_EXEMPLO}
            podeSalvar={podeSalvar}
            aoSalvar={salvarTextos}
            salvando={salvando}
            salvo={salvo}
            desabilitado={rodando}
          />

          <section className="secao">
            <header className="secao-barra">
              <div>
                <h2 className="secao-titulo">Consultas</h2>
                <p className="secao-sub">
                  Marcadas por padrão: as{' '}
                  <strong>
                    {modo === 'reagendamento' ? 'que não compareceram' : 'em aberto'}, com celular
                  </strong>
                  . Quem tem mais de uma consulta no dia recebe uma mensagem só.
                </p>
              </div>
              <div className="item-acoes">
                <button
                  type="button"
                  className="btn-texto"
                  onClick={() => marcarTodas(true)}
                  disabled={rodando}
                >
                  Marcar todas com celular
                </button>
                <button
                  type="button"
                  className="btn-texto"
                  onClick={() => marcarTodas(false)}
                  disabled={rodando}
                >
                  Desmarcar todas
                </button>
              </div>
            </header>

            <div className="tabela-envolve">
              <table className="tabela">
                <thead>
                  <tr>
                    <th aria-label="Confirmar" />
                    <th>Paciente</th>
                    <th>Celular</th>
                    <th>Horário</th>
                    <th>Dentista</th>
                    <th>Status</th>
                    <th>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {consultas.map((c, i) => (
                    <tr key={`${c.siso}-${i}`} className={ativaNoModo(c, modo) ? undefined : 'linha-inativa'}>
                      <td>
                        <input
                          type="checkbox"
                          checked={marcadas.has(i)}
                          // Sem celular não há como confirmar — marcar seria
                          // prometer um envio que não teria destino.
                          disabled={!c.celular || rodando}
                          onChange={() => alternar(i)}
                          aria-label={`Confirmar ${c.paciente}`}
                        />
                      </td>
                      <td>
                        <strong>{c.paciente || '—'}</strong>
                        {c.siso && <span className="canal-instancia">SISO {c.siso}</span>}
                      </td>
                      <td>
                        {c.celular ? (
                          formatarTelefone(c.celular)
                        ) : (
                          <span className="canal-sem-acao">sem celular</span>
                        )}
                      </td>
                      <td>{c.horario}</td>
                      <td>{c.dentista || <span className="canal-sem-acao">—</span>}</td>
                      <td>
                        <span
                          className="pilula pequena"
                          style={{ background: c.emAberto ? '#f59e0b' : '#64748b' }}
                        >
                          {c.status || '—'}
                        </span>
                      </td>
                      <td>{c.tipo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="secao">
            <div className="cartao">
              <div className="item-acoes">
                <button
                  type="button"
                  className="btn-primario"
                  onClick={() => setConfirmando(true)}
                  disabled={rodando || pessoas.length === 0}
                >
                  {rodando
                    ? `Enviando ${envio.atual} de ${envio.total}…`
                    : `Enviar ${verboLote} (${pessoas.length} ${
                        pessoas.length === 1 ? 'pessoa' : 'pessoas'
                      })`}
                </button>
                <span className="campo-ajuda">
                  Sai pelo WhatsApp da recepção, com {PAUSA_MIN_MS / 1000} a{' '}
                  {PAUSA_MAX_MS / 1000} segundos entre um paciente e o próximo.
                </span>
              </div>

              {rodando && (
                <p className="campo-ajuda" style={{ marginTop: 10 }}>
                  Enviando para <strong>{envio.nomeAtual}</strong>… Não feche esta página: sair
                  interrompe o lote.
                </p>
              )}

              {envio && !envio.rodando && (
                <div className="resumo-envio">
                  <p>
                    <strong>{envio.enviadas}</strong> de {envio.total}{' '}
                    {envio.total === 1 ? 'pessoa recebeu' : 'pessoas receberam'} a confirmação.
                    {envio.falhas.length > 0 && (
                      <>
                        {' '}
                        <strong>{envio.falhas.length}</strong> falharam.
                      </>
                    )}
                  </p>
                  {/* Sair da tela para o lote. Dizer isso é o que evita a
                      leitura errada de "3 de 7 receberam" como se as outras 4
                      tivessem falhado — elas nem chegaram a ser tentadas. */}
                  {envio.interrompido && (
                    <p className="campo-ajuda">
                      O lote foi <strong>interrompido</strong> ao sair da tela. Quem ainda não
                      recebeu continua marcado — dá para enviar de novo, e só quem falta recebe
                      se você desmarcar quem já recebeu.
                    </p>
                  )}
                  {envio.falhas.length > 0 && (
                    <ul className="lista-falhas">
                      {envio.falhas.map(({ pessoa, motivo }) => (
                        <li key={pessoa.telefone}>
                          <strong>{pessoa.nomeCompleto}</strong>{' '}
                          {formatarTelefone(pessoa.telefone)} — {motivo}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      <Dialogo
        titulo={`Enviar ${verboLote}?`}
        aberto={confirmando}
        aoFechar={() => setConfirmando(false)}
        largura={520}
      >
        <div className="dialogo-corpo">
          <p>
            <strong>{pessoas.length}</strong>{' '}
            {pessoas.length === 1 ? 'pessoa vai receber' : 'pessoas vão receber'} uma mensagem
            no WhatsApp, pelo número da recepção.
          </p>
          <p className="campo-ajuda">
            Modelo: <strong>{modelos.find((m) => m.id === modelo)?.rotulo}</strong> · Dia:{' '}
            <strong>{dataLonga(dataLote)}</strong>
          </p>
          <p className="previa-balao">
            {montarTexto(textos[campoDoModelo], pessoas[0] ?? PESSOA_EXEMPLO, dataLote)}
          </p>
          <p className="campo-ajuda">
            O envio leva cerca de{' '}
            <strong>
              {Math.max(
                1,
                Math.ceil((pessoas.length * ((PAUSA_MIN_MS + PAUSA_MAX_MS) / 2)) / 60_000),
              )}{' '}
              min
            </strong>
            . Isto envia de verdade e não tem como desfazer.
          </p>
        </div>
        <div className="dialogo-acoes">
          <button type="button" className="btn-texto" onClick={() => setConfirmando(false)}>
            Cancelar
          </button>
          <button type="button" className="btn-primario" onClick={dispararLote}>
            Enviar agora
          </button>
        </div>
      </Dialogo>
    </div>
  );
}
